import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DialogShell from '../../tasks/components/DialogShell';
import { errorText, Group, groupsApi, groupTree, Nature, NATURES, natureLabel } from '../accounting.api';

export interface GroupTarget {
  /** null adds a new group. */
  group: Group | null;
  /** For a new group: its parent (null = a primary group). */
  parentId: number | null;
}

/**
 * Add a group (under a parent, inheriting its nature; or primary, with a nature of its own),
 * or rename / move one. Reserved groups never reach here. A group cannot move under itself.
 */
export default function GroupDialog({
  target,
  companyId,
  groups,
  onClose,
  onSaved,
}: {
  target: GroupTarget | null;
  companyId: number | null;
  groups: Group[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [nature, setNature] = useState<Nature | ''>('');
  const [sortOrder, setSortOrder] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setName(target.group?.name ?? '');
    setParentId((target.group ? target.group.parentId : target.parentId) ?? '');
    setNature(target.group?.nature ?? '');
    setSortOrder(target.group ? String(target.group.sortOrder) : '');
  }, [target]);

  const tree = useMemo(() => groupTree(groups), [groups]);

  // Not itself, nor anything under it.
  const excluded = useMemo(() => {
    const ids = new Set<number>();
    if (!target?.group) return ids;
    ids.add(target.group.id);
    tree.forEach(({ group }) => {
      if (group.parentId && ids.has(group.parentId)) ids.add(group.id);
    });
    return ids;
  }, [target, tree]);

  const parent = groups.find((g) => g.id === parentId);

  const save = async () => {
    if (!companyId || !target || !name.trim() || (parentId === '' && !nature)) return;
    const body = {
      parentId: parentId === '' ? null : parentId,
      name: name.trim(),
      nature: parentId === '' ? (nature as Nature) : null,
      sortOrder: sortOrder.trim() ? Number(sortOrder) : null,
    };
    setBusy(true);
    try {
      if (target.group) await groupsApi.update(companyId, target.group.id, body);
      else await groupsApi.create(companyId, body);
      toast.success(target.group ? 'Group saved.' : 'Group created.');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(e, 'Could not save the group', 'ACCOUNTING_GROUP_SAVE_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell
      open={target !== null}
      title={target?.group ? 'EDIT GROUP' : 'NEW GROUP'}
      onClose={onClose}
      onSubmit={save}
      busy={busy}
      disabled={!name.trim() || (parentId === '' && !nature)}
    >
      <TextField label='Name' size='small' required autoFocus value={name} inputProps={{ maxLength: 100 }} onChange={(e) => setName(e.target.value)} />
      <TextField
        select
        label='Under'
        size='small'
        value={parentId}
        onChange={(e) => setParentId(e.target.value === '' ? '' : Number(e.target.value))}
        helperText={parent ? `Takes its nature from ${parent.name}: ${natureLabel(parent.nature)}${parent.cashBank ? ` (${parent.cashBank.toLowerCase()})` : ''}` : 'A primary group'}
      >
        <MenuItem value=''>
          <em>Primary (no parent)</em>
        </MenuItem>
        {tree
          .filter(({ group }) => !excluded.has(group.id))
          .map(({ group, depth }) => (
            <MenuItem key={group.id} value={group.id} sx={{ pl: 2 + depth * 2, fontWeight: depth === 0 ? 700 : 400 }}>
              {group.name}
            </MenuItem>
          ))}
      </TextField>
      {parentId === '' && (
        <TextField select label='Nature' size='small' required value={nature} onChange={(e) => setNature(e.target.value as Nature)}>
          {NATURES.map((n) => (
            <MenuItem key={n} value={n}>
              {natureLabel(n)}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 2, alignItems: 'center' }}>
        <TextField
          label='Sort order'
          size='small'
          value={sortOrder}
          placeholder='1000'
          inputProps={{ inputMode: 'numeric' }}
          onChange={(e) => /^\d{0,6}$/.test(e.target.value) && setSortOrder(e.target.value)}
        />
        <Typography variant='caption' color='text.secondary'>
          Lower numbers list first; blank keeps the default.
        </Typography>
      </Box>
    </DialogShell>
  );
}
